import { NavLink } from 'react-router-dom';
import { resolveMemberMediaUrl } from '@formosa/shared/utils/mediaUrl';
import { useChat } from '../../chat/ChatProvider';
import { filterSocialImConversations } from '../../chat/socialImConversations';
import { muteLabel, normalizeMuteMode, shouldAlertUnread } from '../../chat/muteHelpers';

export default function ConversationList() {
  const { state, myMemberId } = useChat();
  const { conversationsLoading, conversationsError, participantSummaries } = state;
  const conversations = filterSocialImConversations(state.conversations);

  if (conversationsLoading && !conversations.length) {
    return <div className="chat-state">載入會話中…</div>;
  }
  if (conversationsError && !conversations.length) {
    return <div className="chat-state chat-state--error">{conversationsError}</div>;
  }
  if (!conversations.length) {
    return (
      <div className="chat-state">
        <p>尚無聊天會話</p>
        <p className="chat-state__hint">建立一對一會話後會顯示於此</p>
      </div>
    );
  }

  return (
    <ul className="chat-list">
      {conversations.map((conv) => {
        const id = conv.conversationId || conv.id;
        const title = resolveTitle(conv, participantSummaries, myMemberId);
        const avatar = resolveAvatar(conv, participantSummaries);
        const unread = conv.unreadCount || 0;
        const mode = normalizeMuteMode(conv);
        const alertUnread = shouldAlertUnread(conv);
        const muteText = muteLabel(mode);
        return (
          <li key={id}>
            <NavLink
              to={`/chat/${id}`}
              className={({ isActive }) =>
                `chat-list__item ${isActive ? 'is-active' : ''}`
              }
            >
              <div className="chat-list__avatar" aria-hidden>
                {avatar ? <img src={avatar} alt="" /> : <span>{title.slice(0, 1)}</span>}
              </div>
              <div className="chat-list__body">
                <div className="chat-list__row">
                  <strong className="chat-list__title">
                    {title}
                    {muteText ? (
                      <span className="chat-list__mute" title={muteText}>
                        {mode === 'EXCEPT_MENTION' ? '靜音·@' : '全靜音'}
                      </span>
                    ) : null}
                  </strong>
                  <time className="chat-list__time">
                    {formatListTime(conv.lastMessageAt || conv.lastMessageTime)}
                  </time>
                </div>
                <div className="chat-list__row">
                  <span className="chat-list__preview">
                    {badgeType(conv.conversationType)}
                    {conv.lastMessagePreview || '尚無訊息'}
                  </span>
                  {unread > 0 ? (
                    <span
                      className={`chat-list__unread ${
                        mode === 'ALL' ? 'is-muted' : mode === 'EXCEPT_MENTION' && !alertUnread ? 'is-muted' : ''
                      } ${alertUnread && mode === 'EXCEPT_MENTION' ? 'is-mention' : ''}`}
                    >
                      {unread > 99 ? '99+' : unread}
                    </span>
                  ) : null}
                </div>
              </div>
            </NavLink>
          </li>
        );
      })}
    </ul>
  );
}

export function resolveTitle(conv, summaries, myMemberId) {
  if (conv?.title) return conv.title;
  if (conv?.conversationType === 'DIRECT' && conv.peerMemberId) {
    const peer = summaries[conv.peerMemberId];
    if (peer?.displayName) return peer.displayName;
    return `用戶 ${conv.peerMemberId}`;
  }
  const others = (conv?.participantMemberIds || []).filter((id) => String(id) !== String(myMemberId));
  if (others.length === 1 && summaries[others[0]]?.displayName) {
    return summaries[others[0]].displayName;
  }
  if (conv?.conversationType === 'CUSTOMER_SERVICE') return '線上客服';
  return conv?.conversationType === 'GROUP' ? '群組聊天' : `會話 ${conv?.conversationId || conv?.id}`;
}

function resolveAvatar(conv, summaries) {
  if (conv?.avatarUrl) return resolveMemberMediaUrl(conv.avatarUrl);
  if (conv?.peerMemberId && summaries[conv.peerMemberId]?.avatarUrl) {
    return resolveMemberMediaUrl(summaries[conv.peerMemberId].avatarUrl);
  }
  return '';
}

function badgeType(type) {
  if (type === 'GROUP' || type === 'BUSINESS_GROUP') return '[群] ';
  if (type === 'CUSTOMER_SERVICE') return '[客服] ';
  if (type === 'DIRECT') return '';
  return type ? `[${type}] ` : '';
}

function formatListTime(value) {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  const now = new Date();
  if (d.toDateString() === now.toDateString()) {
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }
  return `${d.getMonth() + 1}/${d.getDate()}`;
}
